import {
  DIRECTED_MAX_CAST,
  type DirectedCastEntry,
  type DirectedCastPicturePending,
  type DirectedCastRole,
} from 'fym-shared/src/music24/directedVideoApi';

/** The photo types of a cast upload (design b1: "JPG or PNG"; WebP works too). */
export const CAST_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** The largest photo of a cast upload. */
export const CAST_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;

export type CastUploadRefusal = 'type' | 'size';

/** Why a picked file cannot go into the cast, or null. Pure. */
export function castUploadRefusal(file: { type: string; size: number }): CastUploadRefusal | null {
  if (!CAST_UPLOAD_TYPES.includes(file.type)) {
    return 'type';
  }
  return file.size > CAST_UPLOAD_MAX_BYTES ? 'size' : null;
}

/** The places of the cast that are taken: the entries, the pictures being made, the uploads. Pure. */
export function castSlotsUsed(
  cast: readonly DirectedCastEntry[],
  pending: readonly DirectedCastPicturePending[],
  uploads: number
): number {
  return cast.length + pending.filter((picture) => picture.status === 'working').length + uploads;
}

/** True while one more picture fits in the cast (at most `DIRECTED_MAX_CAST`). Pure. */
export function castHasRoom(used: number): boolean {
  return used < DIRECTED_MAX_CAST;
}

/**
 * The tick of the consent box when the screen opens: on when an entry of the cast holds a stored
 * consent (the artist ticked it before). Pure.
 */
export function initialConsent(cast: readonly DirectedCastEntry[]): boolean {
  return cast.some((entry) => entry.consentAt !== null && entry.fromArtistPicture !== true);
}

/**
 * True when the cast has a photo of the artist: then the consent box must be ticked to go on (design
 * b4). Every upload needs the tick, whatever its role (an object or a place photo can show a person,
 * review I3); a picture that the AI made and the artist picture need none. Pure.
 */
export function castNeedsConsent(cast: readonly DirectedCastEntry[], pendingPersons: number): boolean {
  return pendingPersons > 0 || cast.some(isUploadedPhoto);
}

/**
 * True for a photo that the artist uploaded: only such a photo needs the consent tick. A picture
 * that Music24 made (`generated`) and the artist picture of the account (`fromArtistPicture`: the
 * choice is the confirmation) need none (owner fix 2026-10-19). Pure.
 */
export function isUploadedPhoto(entry: DirectedCastEntry): boolean {
  return !entry.generated && entry.fromArtistPicture !== true;
}

/**
 * The role of a new upload: Person (most cast photos show a person). An upload goes only with the
 * consent tick (`canUploadCast`); the artist changes the role on the card.
 */
export const CAST_UPLOAD_ROLE: DirectedCastRole = 'person';

/** True when a photo may go up: the consent box is ticked (review I3). Pure. */
export function canUploadCast(consent: boolean): boolean {
  return consent;
}
